import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  CreditCard,
  Gift,
  HelpCircle,
  Headphones,
  Maximize,
  Minimize,
  LogOut,
  BookOpen,
  CircleHelp,
  Shield,
  Moon,
  Sun,
  Crown,
  Zap,
} from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import PosyChatPanel from '@/components/PosyChatPanel';

// Note: We check userRole inside the component to conditionally show admin link

const menuItems = [
  { label: 'Mon compte', icon: User, path: '/mon-compte' },
  { label: 'Mon abonnement', icon: CreditCard, path: '/mon-compte', tab: 'subscription' },
  { label: 'Parrainer un ami', icon: Gift, path: '/mon-compte', tab: 'referral' },
];

const helpItems = [
  { label: 'Aide en ligne', icon: HelpCircle, path: '/tutoriels' },
  { label: "Demande d'assistance", icon: Headphones, path: '/mon-compte', tab: 'support' },
];

export default function UserMenu() {
  const [open, setOpen] = useState(false);
  const [posyOpen, setPosyOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const navigate = useNavigate();
  const { user, userRole, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Utilisateur';

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Bouton Tutoriels (texte) retiré — l'icône Aide ronde ci-dessous suffit */}
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 rounded-full"
          onClick={() => setPosyOpen(true)}
          title="Tutoriels & Aide"
        >
          <CircleHelp className="h-5 w-5 text-primary" />
        </Button>
        <button
          onClick={() => setOpen(true)}
          className="flex min-h-[44px] items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
            <User className="h-4 w-4 text-primary" />
          </div>
          {/* Nom utilisateur retiré du header — visible uniquement dans le sheet ouvert */}
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 rounded-full text-destructive hover:bg-destructive/10"
          onClick={() => setConfirmLogoutOpen(true)}
          title="Se déconnecter"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[340px] p-0">
          <SheetHeader className="p-5 pb-4">
            <SheetTitle className="text-lg font-semibold">{userName}</SheetTitle>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </SheetHeader>

          <div className="px-2">
            {menuItems.map((item) => (
              <button
                key={item.label}
                className="flex w-full items-center gap-4 rounded-lg px-4 py-3.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                onClick={() => {
                  navigate(item.tab ? `${item.path}?tab=${item.tab}` : item.path);
                  setOpen(false);
                }}
              >
                <item.icon className="h-5 w-5 text-primary" />
                <span>{item.label}</span>
              </button>
            ))}

            {userRole === 'super_admin' && (
              <button
                className="flex w-full items-center gap-4 rounded-lg px-4 py-3.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                onClick={() => {
                  navigate('/admin-portal');
                  setOpen(false);
                }}
              >
                <Shield className="h-5 w-5 text-primary" />
                <span>Portail Revendeur</span>
              </button>
            )}
          </div>

          <Separator className="mx-4 my-2" />

          <div className="px-2">
            {helpItems.map((item) => (
              <button
                key={item.label}
                className="flex w-full items-center gap-4 rounded-lg px-4 py-3.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                onClick={() => {
                  navigate(item.tab ? `${item.path}?tab=${item.tab}` : item.path);
                  setOpen(false);
                }}
              >
                <item.icon className="h-5 w-5 text-primary" />
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          <Separator className="mx-4 my-2" />

          <div className="px-2">
            <button
              onClick={toggleFullscreen}
              className="flex w-full items-center gap-4 rounded-lg px-4 py-3.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              {isFullscreen ? (
                <Minimize className="h-5 w-5 text-primary" />
              ) : (
                <Maximize className="h-5 w-5 text-primary" />
              )}
              <span>{isFullscreen ? 'Quitter le plein écran' : 'Mettre en écran normal'}</span>
            </button>
          </div>

          <Separator className="mx-4 my-2" />

          <div className="px-2">
            <button
              onClick={toggleTheme}
              className="flex w-full items-center gap-4 rounded-lg bg-foreground px-4 py-3.5 text-sm font-medium text-background transition-colors hover:opacity-90"
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5" />
              ) : theme === 'dark' ? (
                <Crown className="h-5 w-5 text-amber-400" />
              ) : theme === 'gold' ? (
                <Crown className="h-5 w-5 text-blue-800" />
              ) : theme === 'royal' ? (
                <Zap className="h-5 w-5 text-yellow-400" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
              <span>
                {theme === 'light'
                  ? 'Mode sombre'
                  : theme === 'dark'
                    ? 'Mode doré'
                    : theme === 'gold'
                      ? 'Mode royal'
                      : theme === 'royal'
                        ? 'Mode Nardo'
                        : 'Mode clair'}
              </span>
            </button>
          </div>

          <Separator className="mx-4 my-2" />

          <div className="px-2 pb-4">
            <button
              onClick={() => {
                setOpen(false);
                setConfirmLogoutOpen(true);
              }}
              className="flex w-full items-center gap-4 rounded-lg px-4 py-3.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <LogOut className="h-5 w-5" />
              <span>Se déconnecter</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmLogoutOpen} onOpenChange={setConfirmLogoutOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Se déconnecter de cet appareil ?</AlertDialogTitle>
            <AlertDialogDescription>
              Il faudra retaper l'identifiant et le mot de passe pour revenir. Inutile de se
              déconnecter en fin de journée : la connexion reste active.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Rester connecté</AlertDialogCancel>
            <AlertDialogAction onClick={() => signOut()}>Se déconnecter</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Posy Chat Panel */}
      <PosyChatPanel open={posyOpen} onOpenChange={setPosyOpen} />
    </>
  );
}
